import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
    Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { AuthService } from '../../services/auth.service';
import { ComplianceService } from '../../services/compliance.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { AccountService } from '../../services/account.service';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { Account } from '../../interfaces/accounting.interface';
import {
    REMITTANCE_KINDS, RemittanceKind, SalaryTaxDeposit, SalaryTaxRegisterRow, StatutoryRemittance,
} from '../../interfaces/compliance.interface';
import SearchableSelect from '../common/SearchableSelect';
import AccountAutocomplete from '../common/AccountAutocomplete';
import ScrollableTable from '../common/ScrollableTable';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { KIND_LABEL, money, monthLabel, MONTHS } from './complianceUtils';

const bankLabel = (b: BankAccount) => `${b.bankName} - ${b.accountNumber}`;
const cell = { flex: '1 1 200px', maxWidth: 320 } as const;

const StatutoryRemittances: React.FC = () => {
    const { selectedInstitutionId } = useAuth();
    const institutionId = selectedInstitutionId || AuthService.getSelectedInstitution() || '';
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.COMPLIANCE_MANAGE);
    // The lists need TAX_VIEW (salary tax) and PAYROLL_VIEW (other remittances); each part loads only if allowed.
    const canTax = usePermission(PERMISSIONS.TAX_VIEW);
    const canPayroll = usePermission(PERMISSIONS.PAYROLL_VIEW);
    const thisYear = Number(toLocalIsoDate().slice(0, 4));

    const [tab, setTab] = useState(0);
    const [year, setYear] = useState(thisYear);
    const [kindFilter, setKindFilter] = useState<RemittanceKind | ''>('');
    const [loading, setLoading] = useState(true);
    const [register, setRegister] = useState<SalaryTaxRegisterRow[]>([]);
    const [deposits, setDeposits] = useState<SalaryTaxDeposit[]>([]);
    const [remittances, setRemittances] = useState<StatutoryRemittance[]>([]);
    const [banks, setBanks] = useState<BankAccount[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);

    // deposit dialog
    const [depOpen, setDepOpen] = useState(false);
    const [dep, setDep] = useState({ month: 1, challanNumber: '', challanDate: toLocalIsoDate(), bankAccountId: '', amount: '', notes: '' });
    const [depHint, setDepHint] = useState<string>('');
    // remittance dialog
    const [remOpen, setRemOpen] = useState(false);
    const [rem, setRem] = useState({
        kind: 'PF' as RemittanceKind, month: '', payee: '', referenceNumber: '', remittanceDate: toLocalIsoDate(),
        bankAccountId: '', debitAccountId: '', amount: '', notes: '',
    });
    const [remHint, setRemHint] = useState('');
    // reverse dialog
    const [reverseTarget, setReverseTarget] = useState<{ type: 'DEP' | 'REM'; id: string } | null>(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const [reg, deps, rems] = await Promise.all([
                canTax ? ComplianceService.salaryTaxRegister(institutionId, year) : Promise.resolve([]),
                canTax ? ComplianceService.listSalaryTaxDeposits(institutionId, year) : Promise.resolve([]),
                canPayroll ? ComplianceService.listRemittances(institutionId, kindFilter || undefined, year) : Promise.resolve([]),
            ]);
            setRegister(reg); setDeposits(deps); setRemittances(rems);
        } catch (e) {
            toast.showApiError(e, 'Failed to load remittances');
        } finally {
            setLoading(false);
        }
    }, [institutionId, year, kindFilter, canTax, canPayroll]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!institutionId) return;
        BankAccountService.listByInstitution(institutionId)
            // salary TDS deposits and statutory remittances are paid in BDT: no FC / ERQ accounts (the server refuses them too)
            .then(list => setBanks(list.filter(b => !b.currencyCode || b.currencyCode === 'BDT')))
            .catch(() => setBanks([]));
        AccountService.getAllAccounts().then(setAccounts).catch(() => setAccounts([]));
    }, [institutionId]);

    const openDeposit = async (month: number) => {
        setDep({ month, challanNumber: '', challanDate: toLocalIsoDate(), bankAccountId: '', amount: '', notes: '' });
        setDepHint('');
        setDepOpen(true);
        try {
            const s = await ComplianceService.salaryTaxSuggestion(institutionId, year, month);
            setDep(d => ({ ...d, amount: s.suggested > 0 ? String(s.suggested) : '' }));
            setDepHint(`Deducted ${money(s.deducted)}, already deposited ${money(s.deposited)}${s.note ? `. ${s.note}` : ''}`);
        } catch {
            setDepHint('');
        }
    };
    const submitDeposit = async () => {
        setBusy(true);
        try {
            await ComplianceService.createSalaryTaxDeposit(institutionId, {
                year, month: dep.month, challanNumber: dep.challanNumber.trim(), challanDate: dep.challanDate,
                bankAccountId: dep.bankAccountId, amount: Number(dep.amount), notes: dep.notes.trim() || null,
            });
            toast.showSuccess('Salary tax deposit recorded');
            setDepOpen(false);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to record deposit');
        } finally { setBusy(false); }
    };

    const loadRemSuggestion = async (kind: RemittanceKind, month: string) => {
        if (kind === 'OTHER') { setRemHint(''); return; }
        try {
            const s = await ComplianceService.remittanceSuggestion(institutionId, kind, year, month ? Number(month) : undefined);
            setRem(r => ({ ...r, amount: s.suggested && s.suggested > 0 ? String(s.suggested) : r.amount }));
            setRemHint(`${s.due != null ? `Due ${money(s.due)}, ` : ''}remitted ${money(s.remitted)}${s.note ? `. ${s.note}` : ''}`);
        } catch { setRemHint(''); }
    };
    const submitRemittance = async () => {
        setBusy(true);
        try {
            await ComplianceService.createRemittance(institutionId, {
                kind: rem.kind, year, month: rem.month ? Number(rem.month) : null, payee: rem.payee.trim(),
                referenceNumber: rem.referenceNumber.trim() || null, remittanceDate: rem.remittanceDate,
                bankAccountId: rem.bankAccountId, debitAccountId: rem.debitAccountId, amount: Number(rem.amount),
                notes: rem.notes.trim() || null,
            });
            toast.showSuccess('Remittance recorded');
            setRemOpen(false);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to record remittance');
        } finally { setBusy(false); }
    };

    const submitReverse = async () => {
        if (!reverseTarget) return;
        setBusy(true);
        try {
            if (reverseTarget.type === 'DEP') await ComplianceService.reverseSalaryTaxDeposit(reverseTarget.id, reason.trim());
            else await ComplianceService.reverseRemittance(reverseTarget.id, reason.trim());
            toast.showSuccess('Reversed');
            setReverseTarget(null); setReason('');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to reverse');
        } finally { setBusy(false); }
    };

    return (
        <Box sx={{ p: 2 }} data-help-page="remittances.page">
            <Typography variant="h5" gutterBottom>Statutory Remittances</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField data-help="remittances.year" sx={cell} size="small" type="number" label="Year" value={year}
                    onChange={e => setYear(Number(e.target.value) || thisYear)} />
                {tab === 1 && (
                    <TextField data-help="remittances.kindFilter" sx={cell} size="small" select label="Kind" value={kindFilter}
                        onChange={e => setKindFilter(e.target.value as RemittanceKind | '')}>
                        <MenuItem value="">All</MenuItem>
                        {REMITTANCE_KINDS.map(k => <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>)}
                    </TextField>
                )}
            </Box>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} data-help="remittances.tabs">
                <Tab label="Salary tax (TDS)" />
                <Tab label="PF, WPPF, BWWF and other" />
            </Tabs>
            {!loading && ((tab === 0 && !canTax) || (tab === 1 && !canPayroll)) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {tab === 0 ? 'Salary tax records need tax view access.' : 'These remittances need payroll view access.'}
                </Alert>
            )}
            {loading ? <CircularProgress /> : tab === 0 ? (
                <>
                    <Paper variant="outlined" sx={{ mb: 3 }} data-help-section="remittances.tds.table">
                        <ScrollableTable>
                            <Table size="small">
                                <TableHead><TableRow>
                                    <TableCell data-help="remittances.tds.col.month">Month</TableCell><TableCell align="right" data-help="remittances.tds.col.deducted">Tax deducted</TableCell>
                                    <TableCell align="right" data-help="remittances.tds.col.deposited">Deposited</TableCell><TableCell align="right" data-help="remittances.tds.col.outstanding">Outstanding</TableCell>
                                    <TableCell data-help="remittances.tds.col.challans">Challans</TableCell><TableCell />
                                </TableRow></TableHead>
                                <TableBody>
                                    {register.length === 0 && <TableRow><TableCell colSpan={6}>No payroll tax recorded for {year}.</TableCell></TableRow>}
                                    {register.map(r => (
                                        <TableRow key={`${r.year}-${r.month}`}>
                                            <TableCell>{monthLabel(r.month)}</TableCell>
                                            <TableCell align="right">{money(r.deducted)}</TableCell>
                                            <TableCell align="right">{money(r.deposited)}</TableCell>
                                            <TableCell align="right">{money(r.outstanding)}</TableCell>
                                            <TableCell>{r.challanCount}</TableCell>
                                            <TableCell align="right">
                                                {canManage && r.outstanding > 0 && (
                                                    <Button data-help="remittances.tds.action.deposit" size="small" onClick={() => openDeposit(r.month)}>Record deposit</Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollableTable>
                    </Paper>
                    <Typography variant="subtitle1" gutterBottom>Challans</Typography>
                    <Paper variant="outlined" data-help-section="remittances.challans.table">
                        <ScrollableTable>
                            <Table size="small">
                                <TableHead><TableRow>
                                    <TableCell data-help="remittances.challans.col.challan">Challan</TableCell><TableCell data-help="remittances.challans.col.date">Date</TableCell><TableCell data-help="remittances.challans.col.month">Month</TableCell><TableCell data-help="remittances.challans.col.bank">Bank</TableCell>
                                    <TableCell align="right" data-help="remittances.challans.col.amount">Amount</TableCell><TableCell data-help="remittances.challans.col.status">Status</TableCell><TableCell />
                                </TableRow></TableHead>
                                <TableBody>
                                    {deposits.length === 0 && <TableRow><TableCell colSpan={7}>No deposits yet.</TableCell></TableRow>}
                                    {deposits.map(d => (
                                        <TableRow key={d.id}>
                                            <TableCell>{d.challanNumber}</TableCell>
                                            <TableCell>{formatDate(d.challanDate)}</TableCell>
                                            <TableCell>{monthLabel(d.month)} {d.year}</TableCell>
                                            <TableCell>{d.bankName || ''}</TableCell>
                                            <TableCell align="right">{money(d.amount)}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={d.status === 'POSTED' ? 'Posted' : 'Reversed'} color={d.status === 'POSTED' ? 'success' : 'default'} />
                                                {d.status !== 'POSTED' && d.reversalReason && (
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        Reversed{d.reversedDate ? ` ${formatDate(d.reversedDate)}` : ''}: {d.reversalReason}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {canManage && d.status === 'POSTED' && (
                                                    <Button data-help="remittances.challans.action.reverse" size="small" color="warning" onClick={() => setReverseTarget({ type: 'DEP', id: d.id })}>Reverse</Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollableTable>
                    </Paper>
                </>
            ) : (
                <>
                    {canManage && <Button data-help="remittances.other.record" variant="contained" sx={{ mb: 2 }} onClick={() => {
                        setRem({ kind: 'PF', month: '', payee: '', referenceNumber: '', remittanceDate: toLocalIsoDate(), bankAccountId: '', debitAccountId: '', amount: '', notes: '' });
                        setRemHint(''); setRemOpen(true);
                    }}>Record remittance</Button>}
                    <Paper variant="outlined" data-help-section="remittances.other.table">
                        <ScrollableTable>
                            <Table size="small">
                                <TableHead><TableRow>
                                    <TableCell data-help="remittances.other.col.date">Date</TableCell><TableCell data-help="remittances.other.col.kind">Kind</TableCell><TableCell data-help="remittances.other.col.period">Period</TableCell><TableCell data-help="remittances.other.col.payee">Payee</TableCell>
                                    <TableCell data-help="remittances.other.col.reference">Reference</TableCell><TableCell align="right" data-help="remittances.other.col.amount">Amount</TableCell><TableCell data-help="remittances.other.col.status">Status</TableCell><TableCell />
                                </TableRow></TableHead>
                                <TableBody>
                                    {remittances.length === 0 && <TableRow><TableCell colSpan={8}>No remittances recorded.</TableCell></TableRow>}
                                    {remittances.map(r => (
                                        <TableRow key={r.id}>
                                            <TableCell>{formatDate(r.remittanceDate)}</TableCell>
                                            <TableCell>{KIND_LABEL[r.kind]}</TableCell>
                                            <TableCell>{r.month ? `${monthLabel(r.month)} ` : ''}{r.year}</TableCell>
                                            <TableCell>{r.payee}</TableCell>
                                            <TableCell>{r.referenceNumber || ''}</TableCell>
                                            <TableCell align="right">{money(r.amount)}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={r.status === 'POSTED' ? 'Posted' : 'Reversed'} color={r.status === 'POSTED' ? 'success' : 'default'} />
                                                {r.status !== 'POSTED' && r.reversalReason && (
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        Reversed{r.reversedDate ? ` ${formatDate(r.reversedDate)}` : ''}: {r.reversalReason}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {canManage && r.status === 'POSTED' && (
                                                    <Button data-help="remittances.other.action.reverse" size="small" color="warning" onClick={() => setReverseTarget({ type: 'REM', id: r.id })}>Reverse</Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollableTable>
                    </Paper>
                </>
            )}

            <Dialog open={depOpen} onClose={() => setDepOpen(false)} fullWidth maxWidth="sm" PaperProps={{ 'data-help-page': 'remittances.depositForm.page' } as any}>
                <DialogTitle>Record salary tax deposit - {MONTHS[dep.month - 1]} {year}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    {depHint && <Alert severity="info">{depHint}</Alert>}
                    <TextField label="Challan number" required fullWidth value={dep.challanNumber} data-help="remittances.depositForm.field.challan" onChange={e => setDep({ ...dep, challanNumber: e.target.value })} />
                    <TextField label="Challan date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={dep.challanDate} data-help="remittances.depositForm.field.date" onChange={e => setDep({ ...dep, challanDate: e.target.value })} />
                    <SearchableSelect<BankAccount> label="Bank account" required options={banks} value={dep.bankAccountId} data-help="remittances.depositForm.field.bank"
                        getOptionLabel={bankLabel} onChange={id => setDep({ ...dep, bankAccountId: id })} />
                    <TextField label="Amount" required type="number" fullWidth value={dep.amount} data-help="remittances.depositForm.field.amount" onChange={e => setDep({ ...dep, amount: e.target.value })} />
                    <TextField label="Notes" fullWidth value={dep.notes} data-help="remittances.depositForm.field.notes" onChange={e => setDep({ ...dep, notes: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDepOpen(false)} data-help="remittances.depositForm.cancel">Cancel</Button>
                    <Button variant="contained" disabled={busy || !dep.challanNumber.trim() || !dep.bankAccountId || !(Number(dep.amount) > 0)} data-help="remittances.depositForm.post" onClick={submitDeposit}>Post deposit</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={remOpen} onClose={() => setRemOpen(false)} fullWidth maxWidth="sm" PaperProps={{ 'data-help-page': 'remittances.remitForm.page' } as any}>
                <DialogTitle>Record statutory remittance - {year}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    {remHint && <Alert severity="info">{remHint}</Alert>}
                    <TextField select label="Kind" fullWidth value={rem.kind} data-help="remittances.remitForm.field.kind" onChange={e => {
                        const kind = e.target.value as RemittanceKind;
                        setRem({ ...rem, kind }); loadRemSuggestion(kind, rem.month);
                    }}>
                        {REMITTANCE_KINDS.map(k => <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>)}
                    </TextField>
                    <TextField select label="Month (optional)" fullWidth value={rem.month} data-help="remittances.remitForm.field.month" onChange={e => {
                        setRem({ ...rem, month: e.target.value }); loadRemSuggestion(rem.kind, e.target.value);
                    }}>
                        <MenuItem value="">Whole year</MenuItem>
                        {MONTHS.map((m, i) => <MenuItem key={m} value={String(i + 1)}>{m}</MenuItem>)}
                    </TextField>
                    <TextField label="Payee" required fullWidth value={rem.payee} data-help="remittances.remitForm.field.payee" onChange={e => setRem({ ...rem, payee: e.target.value })} />
                    <TextField label="Reference number" fullWidth value={rem.referenceNumber} data-help="remittances.remitForm.field.reference" onChange={e => setRem({ ...rem, referenceNumber: e.target.value })} />
                    <TextField label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={rem.remittanceDate} data-help="remittances.remitForm.field.date" onChange={e => setRem({ ...rem, remittanceDate: e.target.value })} />
                    <SearchableSelect<BankAccount> label="Bank account" required options={banks} value={rem.bankAccountId} data-help="remittances.remitForm.field.bank"
                        getOptionLabel={bankLabel} onChange={id => setRem({ ...rem, bankAccountId: id })} />
                    <AccountAutocomplete label="Payable account to clear" required accounts={accounts} value={rem.debitAccountId} data-help="remittances.remitForm.field.account"
                        onChange={id => setRem({ ...rem, debitAccountId: id })} />
                    <TextField label="Amount" required type="number" fullWidth value={rem.amount} data-help="remittances.remitForm.field.amount" onChange={e => setRem({ ...rem, amount: e.target.value })} />
                    <TextField label="Notes" fullWidth value={rem.notes} data-help="remittances.remitForm.field.notes" onChange={e => setRem({ ...rem, notes: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemOpen(false)} data-help="remittances.remitForm.cancel">Cancel</Button>
                    <Button variant="contained" disabled={busy || !rem.payee.trim() || !rem.bankAccountId || !rem.debitAccountId || !(Number(rem.amount) > 0)} data-help="remittances.remitForm.post" onClick={submitRemittance}>Post remittance</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!reverseTarget} onClose={() => setReverseTarget(null)} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'remittances.reverse.page' } as any}>
                <DialogTitle>Reverse posting</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField label="Reason" required fullWidth multiline value={reason} data-help="remittances.reverse.field.reason" onChange={e => setReason(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReverseTarget(null)} data-help="remittances.reverse.cancel">Cancel</Button>
                    <Button variant="contained" color="warning" disabled={busy || !reason.trim()} data-help="remittances.reverse.confirm" onClick={submitReverse}>Reverse</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StatutoryRemittances;
